import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Scale, LayoutDashboard, Users, BarChart3, Gavel, LogOut, Database, Menu, X, Trash2, UserCheck, Inbox } from "lucide-react";
import { api } from "@/App";

export default function TopNav({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendientes, setPendientes] = useState(0);

  useEffect(() => {
    if (user?.actuacion !== "CEO") return;
    const fetchResumen = async () => {
      try {
        const res = await api.get("/admin/aprobaciones/resumen");
        setPendientes((res.data.hechos_pendientes || 0) + (res.data.casos_pendientes || 0));
      } catch {}
    };
    fetchResumen();
    const t = setInterval(fetchResumen, 60000);
    return () => clearInterval(t);
  }, [user]);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const allLinks = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/abogados", label: "Abogados", icon: UserCheck },
    { path: "/papelera", label: "Papelera", icon: Trash2 },
    { path: "/data-lake", label: "Data Lake", icon: Database },
    { path: "/admin/usuarios", label: "Usuarios", icon: Users },
    { path: "/admin/logs", label: "Auditoría", icon: BarChart3 },
    { path: "/admin/fases", label: "Fases y Procesos", icon: Gavel },
    ...(user?.actuacion === "CEO" ? [{ path: "/admin/aprobaciones", label: "Aprobaciones", icon: Inbox, badge: pendientes }] : []),
  ];

  const handleNav = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  return (
    <header className="bg-white border-b sticky top-0 z-50" style={{ borderColor: "var(--border)" }}>
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-14">
          {/* Logo + Nav */}
          <div className="flex items-center gap-6">
            {/* Logo */}
            <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "var(--primary)" }}>
                <Scale className="h-4 w-4 text-white" />
              </div>
              <span className="font-bold text-sm hidden sm:inline" style={{ color: "var(--text)" }}>LegalStory</span>
            </button>

            {/* Divider */}
            <div className="w-px h-6 hidden md:block" style={{ background: "var(--border)" }} />

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
              {allLinks.map(link => (
                <button
                  key={link.path}
                  onClick={() => handleNav(link.path)}
                  className={`nav-link ${isActive(link.path) ? "active" : ""}`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <link.icon className="h-4 w-4" />
                  <span>{link.label}</span>
                  {link.badge > 0 && (
                    <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="aprobaciones-badge">{link.badge}</span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* User + Mobile toggle */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: "var(--primary)" }}>
                {(user?.nombre_completo || user?.name || user?.email || "U")[0].toUpperCase()}
              </div>
              <div className="hidden sm:block">
                <p className="text-xs font-medium leading-none" style={{ color: "var(--text)" }}>
                  {user?.nombre_completo || user?.name || user?.email}
                </p>
                <p className="text-[10px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                  {user?.actuacion || user?.rol || "Usuario"}
                </p>
              </div>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-lg transition-colors"
              style={{ color: "var(--text-muted)" }}
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

            {/* Desktop logout */}
            <button
              onClick={onLogout}
              className="hidden md:block p-2 rounded-lg transition-colors hover:bg-red-50"
              style={{ color: "var(--text-muted)" }}
              title="Cerrar sesión"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 hover:text-red-500" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
          <nav className="container mx-auto px-6 py-3 space-y-1" data-testid="mobile-nav">
            {allLinks.map(link => (
              <button
                key={link.path}
                onClick={() => handleNav(link.path)}
                className={`nav-link w-full ${isActive(link.path) ? "active" : ""}`}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <link.icon className="h-4 w-4" />
                <span>{link.label}</span>
              </button>
            ))}
            <div className="pt-2 border-t" style={{ borderColor: "var(--border)" }}>
              <button onClick={onLogout} className="nav-link w-full text-red-500 hover:bg-red-50" data-testid="mobile-logout-btn">
                <LogOut className="h-4 w-4" />
                <span>Cerrar sesión</span>
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
